<template>
    <div class="flex items-start gap-[35px]">
        <div class="flex-1 rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
            <div
                class="wizard-topbar flex items-center justify-between gap-[24px] border-b border-gray-200 px-[21px] py-[21px]"
            >
                <Stepper
                    class="wizard-stepper"
                    :steps="steps"
                    :current-step="currentStep"
                />

                <div class="wizard-actions flex items-center gap-[12px]">
                    <button
                        v-if="canGoBack"
                        type="button"
                        class="cursor-pointer inline-flex h-[28px] items-center gap-[6px] rounded-[6px] border border-gray-300 bg-white px-[12px] text-[10.5px] font-medium leading-[140%] text-gray-700 hover:bg-gray-50"
                        @click="goBack"
                    >
                        <ArrowLeft class="h-4 w-4" />
                        {{ $t("vue.back") }}
                    </button>

                    <button
                        v-if="showExitSetup"
                        type="button"
                        class="cursor-pointer inline-flex h-[28px] items-center rounded-[6px] border-[0.365px] border-[#FF4040] bg-white px-[14px] py-[10px] text-[10.5px] font-medium leading-[140%] text-[#FF4040] hover:bg-red-50"
                        @click="exitSetup"
                    >
                        {{ t("vue.domain_wizard_exit_setup") }}
                    </button>

                    <button
                        v-if="showFinishLater"
                        type="button"
                        class="cursor-pointer inline-flex h-[28px] items-center gap-[6px] rounded-[6px] border border-gray-300 bg-white px-[12px] text-[10.5px] font-medium leading-[140%] text-gray-700 hover:bg-gray-50"
                        @click="exitSetup"
                    >
                        <span class="wizard-action-label-full">
                            {{ t("vue.finish_later") }}
                        </span>
                        <span class="wizard-action-label-compact">
                            {{ t("vue.finish") }}
                        </span>
                    </button>

                    <div
                        v-if="showVerifyDnsMenu"
                        class="inline-flex items-center"
                    >
                        <button
                            type="button"
                            class="cursor-pointer btn-hover-dim inline-flex h-[28px] items-center justify-center gap-[3.65px] rounded-l-[6px] rounded-r-none px-[14px] py-[10px] text-[10.5px] font-medium leading-[140%] text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                            :class="primaryBtnClass"
                            :disabled="primaryDisabled"
                            :aria-disabled="
                                (
                                    primaryDisabled ||
                                    domainManipulationRestricted
                                ).toString()
                            "
                            :title="primaryDisabledMessage"
                            @click="goNextOrFinish"
                        >
                            <svg
                                class="h-[16px] w-[16px]"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                            >
                                <path
                                    d="M2.70923 7.33325C2.682 7.55165 2.66797 7.77412 2.66797 7.99992C2.66797 10.9454 5.05578 13.3333 8.0013 13.3333C9.66837 13.3333 11.1568 12.5684 12.1348 11.3705M12.1348 11.3705V13.3333M12.1348 11.3705V11.3333L10.1348 11.3333M13.2934 8.66659C13.3206 8.44819 13.3346 8.22572 13.3346 7.99992C13.3346 5.05439 10.9468 2.66659 8.0013 2.66659C6.40838 2.66659 4.97856 3.36492 4.0013 4.47219M4.0013 4.47219V4.66659H6.0013M4.0013 4.47219V2.66659"
                                    stroke="white"
                                    stroke-width="1.33333"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            <template v-if="dnsRefreshBusy">
                                {{ t("vue.checking_progress") }}
                            </template>
                            <template v-else>
                                <span class="wizard-action-label-full">
                                    {{ t("vue.verify_dns_records") }}
                                </span>
                                <span class="wizard-action-label-compact">
                                    {{ t("vue.verify") }}
                                </span>
                            </template>
                        </button>
                        <div class="relative group inline-flex">
                            <button
                                type="button"
                                class="relative inline-flex h-[28px] w-[28px] items-center justify-center rounded-r-[6px] rounded-l-none border-l-0 py-[10px] text-white"
                                :class="primaryBtnClass"
                                aria-haspopup="true"
                            >
                                <span
                                    class="absolute left-0 top-[6px] bottom-[6px] w-px bg-white/40"
                                    aria-hidden="true"
                                ></span>
                                <span
                                    class="flex flex-col items-center justify-center gap-[2px]"
                                    aria-hidden="true"
                                >
                                    <span
                                        class="h-[2px] w-[2px] rounded-full bg-white"
                                    ></span>
                                    <span
                                        class="h-[2px] w-[2px] rounded-full bg-white"
                                    ></span>
                                    <span
                                        class="h-[2px] w-[2px] rounded-full bg-white"
                                    ></span>
                                </span>
                            </button>
                            <div
                                class="absolute right-0 top-full z-30 mt-[6px] w-[160px] rounded-[8px] border border-gray-200 bg-white py-[6px] opacity-0 pointer-events-none shadow-[0_8px_24px_rgba(16,24,40,0.12),0_2px_8px_rgba(16,24,40,0.06)] transition group-hover:opacity-100 group-hover:pointer-events-auto"
                            >
                                <span
                                    class="absolute -top-[8px] left-0 right-0 h-[8px]"
                                    aria-hidden="true"
                                ></span>
                                <button
                                    type="button"
                                    class="w-full px-[12px] py-[8px] text-left text-[12px] leading-[140%] text-gray-700 hover:bg-gray-50"
                                    @click="exitSetup"
                                >
                                    {{ t("vue.finish_later") }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <button
                        v-else
                        type="button"
                        class="cursor-pointer btn-hover-dim inline-flex h-[28px] items-center justify-center gap-[3.65px] rounded-[6px] px-[14px] py-[10px] text-[10.5px] font-medium leading-[140%] text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                        :class="primaryBtnClass"
                        :disabled="primaryDisabled"
                        :aria-disabled="
                            (
                                primaryDisabled || domainManipulationRestricted
                            ).toString()
                        "
                        :title="primaryDisabledMessage"
                        @click="goNextOrFinish"
                    >
                        <template v-if="currentStep === 1">
                            {{
                                domainExists
                                    ? t("vue.domain_wizard_continue")
                                    : t("vue.domain_wizard_add_domain")
                            }}
                            <ArrowRight class="h-4 w-4" />
                        </template>

                        <template v-else-if="currentStep === 2">
                            <span class="wizard-action-label-full">
                                {{ $t("vue.validate_records") }}
                            </span>
                            <span class="wizard-action-label-compact">
                                {{ $t("vue.validate") }}
                            </span>
                            <ArrowRight class="h-4 w-4" />
                        </template>

                        <template v-else>
                            <template v-if="dnsValidated">
                                {{ t("vue.finish_setup") }}
                            </template>
                            <template v-else>
                                <svg
                                    class="h-[16px] w-[16px]"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M2.70923 7.33325C2.682 7.55165 2.66797 7.77412 2.66797 7.99992C2.66797 10.9454 5.05578 13.3333 8.0013 13.3333C9.66837 13.3333 11.1568 12.5684 12.1348 11.3705M12.1348 11.3705V13.3333M12.1348 11.3705V11.3333L10.1348 11.3333M13.2934 8.66659C13.3206 8.44819 13.3346 8.22572 13.3346 7.99992C13.3346 5.05439 10.9468 2.66659 8.0013 2.66659C6.40838 2.66659 4.97856 3.36492 4.0013 4.47219M4.0013 4.47219V4.66659H6.0013M4.0013 4.47219V2.66659"
                                        stroke="white"
                                        stroke-width="1.33333"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                                <template v-if="dnsRefreshBusy">
                                    {{ t("vue.checking_progress") }}
                                </template>
                                <template v-else>
                                    <span class="wizard-action-label-full">
                                        {{ t("vue.verify_dns_records") }}
                                    </span>
                                    <span class="wizard-action-label-compact">
                                        {{ t("vue.verify") }}
                                    </span>
                                </template>
                            </template>
                        </template>
                    </button>
                </div>
            </div>

            <div class="px-8 py-10">
                <!-- STEP 1 - ADD YOUR DOMAIN -->
                <section v-if="currentStep === 1" class="mx-auto max-w-[558px]">
                    <div class="text-left">
                        <h2
                            class="text-[15.75px] font-medium leading-[142%]"
                            style="color: #161e2e"
                        >
                            {{ $t("vue.enter_your_domain") }}
                        </h2>
                        <p
                            class="mt-[4px] text-[12.25px] leading-[142%]"
                            style="color: #6b7280"
                        >
                            {{
                                $t("vue.enter_your_domain_to_start_monitoring")
                            }}
                        </p>
                    </div>

                    <div
                        v-if="domainManipulationRestricted"
                        class="mt-[24px] rounded-[12.8px] border border-[#FDE68A] bg-[#FFFBEB] px-5 py-4"
                    >
                        <div class="flex items-start gap-3 text-[#744210]">
                            <Info
                                class="mt-0.5 h-5 w-5 shrink-0 text-[#D97706]"
                            />
                            <div class="flex-1">
                                <p class="text-sm font-medium leading-6">
                                    {{
                                        t(
                                            "vue.domain_wizard_role_restricted_title",
                                        )
                                    }}
                                </p>
                                <p
                                    class="mt-[4px] text-[12.25px] leading-[142%]"
                                >
                                    {{
                                        t(
                                            "vue.domain_wizard_role_restricted_hint",
                                        )
                                    }}
                                </p>
                                <button
                                    v-if="!isFreePlanLevel"
                                    type="button"
                                    class="mt-[12px] inline-flex h-[28px] items-center gap-[6px] rounded-[6px] bg-[#088BEA] px-[14px] text-[10.5px] font-medium leading-[140%] text-white transition hover:opacity-90"
                                    @click="goToTeamMembers"
                                >
                                    {{
                                        t(
                                            "vue.domain_wizard_role_restricted_open_members",
                                        )
                                    }}
                                    <ArrowRight class="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div
                        class="mt-[24px] rounded-[7px] border border-[#E5E7EB] bg-[#F9FAFB] p-[11px]"
                    >
                        <div
                            class="flex h-[35px] items-center gap-[10px] rounded-[7px] border border-[#DDE0E5] bg-white px-[11px] py-[7px]"
                        >
                            <input
                                v-model.trim="domain"
                                type="text"
                                inputmode="url"
                                :placeholder="t('vue.my_beatiful_domain_co')"
                                class="flex-1 bg-transparent text-[12.25px] leading-[142%] text-[#161E2E] placeholder:text-[#9CA3AF] focus:outline-none"
                                @blur="domainTouched = true"
                                @keydown.enter.prevent="findDomain"
                            />

                            <button
                                type="button"
                                class="inline-flex h-[21px] min-w-[62px] items-center justify-center rounded-[4px] bg-[#161E2E] px-[10px] text-[10.5px] font-medium leading-[140%] text-white disabled:opacity-50"
                                :disabled="domainCheckBusy || !domain"
                                @click="findDomain"
                            >
                                {{
                                    domainCheckBusy
                                        ? t("vue.loading")
                                        : t("vue.domain_wizard_find_domain")
                                }}
                            </button>
                        </div>
                    </div>

                    <div
                        v-if="
                            (domainTouched || step1Error) &&
                            (step1Error || !isValidDomain(domain))
                        "
                        class="mt-[14px] flex items-start gap-3 rounded-[12.8px] border border-red-300 bg-red-100 px-5 py-4 text-red-600"
                    >
                        <svg
                            class="mt-0.5 h-5 w-5 shrink-0"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true"
                        >
                            <path
                                d="M10.3 3.6 1.8 18.2A2 2 0 0 0 3.5 21h17a2 2 0 0 0 1.7-2.8L13.7 3.6a2 2 0 0 0-3.4 0Z"
                            />
                            <path d="M12 9v4" />
                            <path d="M12 17h.01" />
                        </svg>

                        <div class="text-sm font-medium leading-6">
                            {{ step1Error || $t("vue.invalid_domain_format") }}
                        </div>
                    </div>

                    <div
                        v-if="domainExists"
                        class="mt-[14px] rounded-[12.8px] border border-[#FDE68A] bg-[#FFFBEB] px-5 py-4"
                    >
                        <div class="flex items-start gap-3 text-[#744210]">
                            <Info
                                class="mt-0.5 h-5 w-5 shrink-0 text-[#D97706]"
                            />
                            <div class="flex-1">
                                <p class="text-sm font-medium leading-6">
                                    {{
                                        t(
                                            "vue.domain_wizard_domain_exists_in_account",
                                            { domain: domain },
                                        )
                                    }}
                                </p>
                                <p
                                    class="mt-[4px] text-[12.25px] leading-[142%]"
                                >
                                    {{
                                        t(
                                            "vue.domain_wizard_domain_exists_open_hint",
                                        )
                                    }}
                                </p>
                                <button
                                    type="button"
                                    class="mt-[12px] inline-flex h-[28px] items-center gap-[6px] rounded-[6px] bg-[#088BEA] px-[14px] text-[10.5px] font-medium leading-[140%] text-white transition hover:opacity-90"
                                    @click="goToExistingDomain"
                                >
                                    {{
                                        t(
                                            "vue.domain_wizard_open_existing_domain",
                                        )
                                    }}
                                    <ArrowRight class="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="domainApproved && !billingLimitReached"
                        class="mt-[24px] flex items-center gap-[5px] rounded-[8px] bg-[#EEFFF2] px-[21px] py-[21px] text-[10.5px] font-medium leading-[140%]"
                        style="color: #276749"
                    >
                        <CheckCircle2
                            class="h-[11px] w-[11px]"
                            style="color: #00c951"
                        />
                        <span>
                            {{
                                t("vue.domain_wizard_domain_approved", {
                                    domain: domain,
                                })
                            }}
                        </span>
                    </div>

                    <DomainWizardBillingUpdate
                        v-if="showBillingUpdate && domainApproved"
                        v-model="billingAgreement"
                        class="mt-[24px]"
                        :title="billingUpdateTitle"
                        :message="billingUpdateCopy"
                        :items="billingLimitReached ? [] : billingUpdateItems"
                        :agreement-text="
                            billingLimitReached
                                ? undefined
                                : t('vue.domain_wizard_billing_agreement')
                        "
                        :variant="billingLimitReached ? 'danger' : 'warning'"
                        :show-items="!billingLimitReached"
                        :show-agreement="!billingLimitReached"
                    />
                </section>

                <!-- STEP 2 - CONFIGURE DNS RECORD -->
                <section
                    v-else-if="currentStep === 2"
                    class="mx-auto max-w-4xl"
                >
                    <div class="text-left">
                        <h2
                            class="text-[15.75px] font-medium leading-[142%] text-[#161E2E]"
                        >
                            {{ $t("vue.configure_dmarc_and_add_dns_record") }}
                            <span class="text-[#088BEA]">
                                {{
                                    domain ||
                                    t("vue.domain_wizard_example_domain")
                                }}
                            </span>
                        </h2>
                        <p
                            class="mt-[4px] text-[10.5px] leading-[142%] text-[#4B5563]"
                        >
                            {{ $t("vue.set_your_dmarc_policy") }}
                        </p>
                    </div>

                    <div class="mt-[24px] space-y-[24px] pb-[24px]">
                        <DmarcRecordConfigurator
                            v-model="txtRecordPreview"
                            host-name="_dmarc"
                        />

                        <DnsProviderSetBlock
                            :domain-name="domain"
                            :desired-dmarc-record="txtRecordPreview"
                            :show-one-click-block="isCloudflare"
                        />

                        <EmailInstructions
                            :domain="domain"
                            :txt-record="txtRecordPreview"
                            title-key="vue.email_instructions_to_developer"
                        />
                    </div>
                </section>

                <!-- STEP 3 - REVIEW & CONFIRM -->
                <section v-else class="mx-auto max-w-3xl">
                    <div class="text-left">
                        <h2
                            class="text-[15.75px] font-medium leading-[142%] text-[#161E2E]"
                        >
                            {{ $t("vue.review_and_confirm") }}
                        </h2>
                        <p
                            class="mt-[4px] text-[10.5px] leading-[142%] text-[#4B5563]"
                        >
                            {{
                                $t("vue.review_dmarc_conf_before_go_to_reports")
                            }}
                        </p>
                    </div>

                    <div class="mt-[24px] space-y-[24px]">
                        <div
                            class="rounded-[8px] border-[1px] border-[#E5E7EB] bg-[#FBFBFB] px-[21px] py-[21px]"
                        >
                            <div
                                class="grid grid-cols-1 gap-[24px] md:grid-cols-4"
                            >
                                <div>
                                    <p
                                        class="text-[12px] font-medium leading-[140%] text-[#000000]"
                                    >
                                        {{ $t("vue.domain_label") }}
                                    </p>
                                    <p
                                        class="mt-[4px] text-[10.5px] leading-[142%] text-[#4B5563]"
                                    >
                                        {{ domain }}
                                    </p>
                                </div>

                                <div>
                                    <p
                                        class="text-[12px] font-medium leading-[140%] text-[#000000]"
                                    >
                                        {{ $t("vue.created_label") }}
                                    </p>
                                    <p
                                        class="mt-[4px] text-[10.5px] leading-[142%] text-[#4B5563]"
                                    >
                                        {{ createdAt }}
                                    </p>
                                </div>

                                <div>
                                    <p
                                        class="text-[12px] font-medium leading-[140%] text-[#000000]"
                                    >
                                        {{ $t("vue.dmarc_policy") }}
                                    </p>
                                    <span
                                        v-if="existingDomain?.dmarcRecord"
                                        class="policy-badge mt-[4px] capitalize"
                                        :class="{
                                            'policy-reject':
                                                dmarcPolicyFromDb === 'reject',
                                            'policy-quarantine':
                                                dmarcPolicyFromDb ===
                                                'quarantine',
                                            'policy-none':
                                                dmarcPolicyFromDb !==
                                                    'reject' &&
                                                dmarcPolicyFromDb !==
                                                    'quarantine',
                                        }"
                                    >
                                        {{ dmarcPolicyFromDb }}
                                    </span>
                                    <span
                                        v-else
                                        class="policy-badge policy-none mt-[4px] capitalize"
                                    >
                                        {{ t("vue.none") }}
                                    </span>
                                </div>

                                <div>
                                    <p
                                        class="text-[12px] font-medium leading-[140%] text-[#000000]"
                                    >
                                        {{ $t("vue.status") }}
                                    </p>
                                    <span
                                        class="policy-badge mt-[4px]"
                                        :class="
                                            dnsValidated
                                                ? 'badge-active'
                                                : 'badge-inactive'
                                        "
                                    >
                                        {{
                                            dnsValidated
                                                ? t("vue.active")
                                                : t("vue.inactive")
                                        }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div
                            class="rounded-[8px] border-[1px] px-[21px] py-[18px]"
                            :class="
                                dnsValidated
                                    ? 'border-[#BBF7D0] bg-[#F0FDF4]'
                                    : 'border-[#FDE68A] bg-[#FFFBEB]'
                            "
                        >
                            <div class="flex items-start gap-[7px]">
                                <CheckCircle2
                                    v-if="dnsValidated"
                                    class="mt-[2px] h-[16px] w-[16px] text-[#00C951]"
                                />
                                <span
                                    v-else
                                    class="mt-[6px] inline-block h-[6px] w-[6px] rounded-full bg-[#D97706]"
                                    aria-hidden="true"
                                />
                                <div class="flex-1">
                                    <p
                                        class="text-[12.25px] font-medium leading-[142%]"
                                        :class="
                                            dnsValidated
                                                ? 'text-[#22543D]'
                                                : 'text-[#744210]'
                                        "
                                    >
                                        {{
                                            dnsValidated
                                                ? t(
                                                      "vue.dns_record_validated_successfully",
                                                  )
                                                : t("vue.waiting_for_records")
                                        }}
                                    </p>

                                    <p
                                        class="mt-[5.5px] text-[10.5px] leading-[142%]"
                                        :class="
                                            dnsValidated
                                                ? 'text-[#276749]'
                                                : 'text-[#744210]'
                                        "
                                    >
                                        <template v-if="dnsValidated">
                                            {{
                                                $t(
                                                    "vue.dmarc_dns_validated_txt_instruction",
                                                )
                                            }}
                                        </template>
                                        <template v-else>
                                            {{
                                                $t("vue.dmarc_txt_instruction")
                                            }}
                                        </template>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>

        <!-- RIGHT SIDEBAR -->
        <aside class="wizard-sidebar hidden w-[340px] shrink-0 lg:block">
            <div class="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
                <div
                    class="flex items-start gap-2 border-b border-gray-200 px-[21px] py-[21px] h-[74.5px]"
                >
                    <Lightbulb class="mt-[2px] h-4 w-4 text-gray-800" />
                    <div>
                        <div
                            class="text-[14px] font-medium leading-[142%]"
                            style="color: #161e2e"
                        >
                            {{ t("vue.domain_wizard_setup_title") }}
                        </div>
                        <div
                            class="text-[10px] font-normal leading-[142%]"
                            style="color: #161e2e"
                        >
                            {{ t("vue.domain_wizard_setup_subtitle") }}
                        </div>
                    </div>
                </div>

                <div v-if="showCompletionNotice" class="px-[14px] py-[14px]">
                    <div class="flex items-start gap-[7px]">
                        <CheckCircle2
                            class="mt-[2px] h-[14px] w-[14px] text-[#00C951]"
                        />
                        <div
                            class="text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                        >
                            {{ t("vue.domain_wizard_domain_activated") }}
                        </div>
                    </div>
                </div>

                <template v-else>
                    <div class="px-6 py-5">
                        <div class="space-y-3">
                            <div
                                v-for="(it, idx) in sidebarItems"
                                :key="idx"
                                class="flex items-start gap-2"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="3"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="h-[11px] w-[11px] shrink-0 text-[#00C951] top-[4px] relative"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M21.801 10A10 10 0 1 1 17 3.335"
                                    ></path>
                                    <path d="m9 11 3 3L22 4"></path>
                                </svg>
                                <div
                                    class="text-[12.25px] font-normal leading-[142%]"
                                    style="color: #4b5563"
                                >
                                    {{ it }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="h-px bg-gray-200" />

                    <div class="px-6 py-6 text-center">
                        <div
                            class="mx-auto h-[62px] w-[62px] rounded-full bg-gray-100 bg-cover bg-center bg-no-repeat"
                            style="
                                background-image: url(&quot;https://dmarceye.s3.eu-west-1.amazonaws.com/application/images/jakub-portrait.jpg&quot;);
                            "
                        ></div>
                        <div
                            class="mt-3 text-[14px] font-medium leading-[142%]"
                            style="color: #161e2e"
                        >
                            {{ t("vue.domain_wizard_need_help") }}
                        </div>

                        <div
                            class="mx-auto mt-2 max-w-[280px] text-[12.25px] font-normal leading-[142%]"
                            style="color: #4b5563"
                        >
                            {{ t("vue.domain_wizard_help_text") }}
                        </div>

                        <a :href="demoCallUrl" target="_blank">
                            <AtomicButton
                                class="mt-2 h-[31.5px] px-4 text-[11px] hover:bg-black hover:text-white transition"
                                color="colorless"
                            >
                                {{ $t("vue.schedule_a_call") }}
                            </AtomicButton>
                        </a>
                    </div>
                </template>
            </div>
        </aside>
    </div>

    <!-- mobile sidebar -->
    <div class="mt-8 lg:hidden">
        <div class="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
            <div
                class="flex items-start gap-2 border-b border-gray-200 px-[21px] py-[21px]"
            >
                <Lightbulb class="mt-[2px] h-4 w-4 text-gray-800" />
                <div>
                    <div
                        class="text-[14px] font-medium leading-[142%]"
                        style="color: #161e2e"
                    >
                        {{ t("vue.domain_wizard_setup_title") }}
                    </div>
                    <div
                        class="text-[10px] font-normal leading-[142%]"
                        style="color: #161e2e"
                    >
                        {{ t("vue.domain_wizard_setup_subtitle") }}
                    </div>
                </div>
            </div>

            <div v-if="showCompletionNotice" class="px-[14px] py-[14px]">
                <div class="flex items-start gap-[7px]">
                    <CheckCircle2
                        class="mt-[2px] h-[14px] w-[14px] text-[#00C951]"
                    />
                    <div
                        class="text-[12.25px] font-normal leading-[142%] text-[#4B5563]"
                    >
                        {{ t("vue.domain_wizard_domain_activated") }}
                    </div>
                </div>
            </div>

            <template v-else>
                <div class="px-6 py-5">
                    <div class="space-y-3">
                        <div
                            v-for="(it, idx) in sidebarItems"
                            :key="idx"
                            class="flex items-start gap-2"
                        >
                            <CheckCircle2
                                class="mt-[2px] h-3 w-3 text-green-500/80"
                            />
                            <div
                                class="text-[12.25px] font-normal leading-[142%]"
                                style="color: #4b5563"
                            >
                                {{ it }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="h-px bg-gray-200" />

                <div class="px-6 py-6 text-center">
                    <div
                        class="mx-auto flex h-[62px] w-[62px] items-center justify-center rounded-full bg-gray-100"
                    >
                        <UserRound class="h-7 w-7 text-gray-700" />
                    </div>

                    <div
                        class="mt-3 text-[14px] font-medium leading-[142%]"
                        style="color: #161e2e"
                    >
                        {{ t("vue.domain_wizard_need_help") }}
                    </div>

                    <div
                        class="mx-auto mt-2 max-w-[280px] text-[12.25px] font-normal leading-[142%]"
                        style="color: #4b5563"
                    >
                        {{ t("vue.domain_wizard_help_text") }}
                    </div>

                    <a
                        :href="demoCallUrl"
                        target="_blank"
                        class="mt-4 inline-flex h-[31.5px] w-full items-center justify-center rounded-md border border-gray-300 bg-white px-4 text-[11px] font-medium text-gray-800 transition hover:bg-black hover:text-white"
                    >
                        {{ t("vue.schedule_a_call") }}
                    </a>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    ArrowLeft,
    ArrowRight,
    Lightbulb,
    CheckCircle2,
    Info,
    UserRound,
} from "lucide-vue-next";
import route from "ziggy-js";
import { router, usePage } from "@inertiajs/vue3";
import {
    computed,
    defineComponent,
    nextTick,
    onMounted,
    ref,
    watch,
} from "vue";
import axios from "axios";
import { useTranslations } from "@/composables/useTranslations.ts";
import { useDmarcRecordPreparer } from "@/composables/useDmarcRecordPreparer";
import EmailInstructions from "@/components/Special/EmailInstructions.vue";
import DomainWizardBillingUpdate from "@/components/Special/DomainWizardBillingUpdate.vue";
import type { IPaddleSubscription } from "@/types/Subscriptions/IPaddleSubscription";
import useDomainManipulationPermissions from "@/composables/UseDomainManipulationPermissions";
import {
    useDomainWizardState,
    type IWizardDomain,
} from "@/composables/useDomainWizardState";

const { t } = useTranslations();
const {
    canManipulateDomains,
    domainManipulationDisabledMessage,
    ensureCanManipulateDomains,
} = useDomainManipulationPermissions();
const page = usePage();
const demoCallUrl = computed(() =>
    (page.props as { locale?: string }).locale === "cs"
        ? "https://dmarceye.com/cs/democall"
        : "https://dmarceye.com/talk-to-sales",
);

type Step = { key: number; title: string };

const {
    currentStep,
    domain,
    domainCheckStatus,
    existingDomain,
    dnsValidated,
    lastLookedUpDomain,
    txtRecordFromServer,
    txtRecordPreview,
    lastPreparedRecord,
    domainCheckBusy,
    domainApproved,
    domainExists,
    resetWizardState,
} = useDomainWizardState();

resetWizardState();

const loading = ref(false);
const domainTouched = ref(false);
const step1Error = ref("");
const billingAgreement = ref(false);
const subscriptionLoading = ref(false);
const subscriptionData = ref<IPaddleSubscription | null>(null);

type DomainDto = IWizardDomain;
const props = defineProps<{
    domainsCount?: number;
    unparkedDomainCount?: number;
    domains?: DomainDto[];
    uuid: string;
    isCloudflare: boolean;
    requestedDomain?: string | null;
    selectedDomain?: DomainDto | null;
    isTrialing?: boolean;
    isTrialExtended?: boolean;
    isScalePlan?: boolean;
    subscriptionLevel?: string | null;
    subscriptionPeriod?: string | null;
    emptySlots?: number;
}>();

const Stepper = defineComponent({
    name: "Stepper",
    components: {},
    props: {
        steps: {
            type: Array as () => Array<{ key: number; title: string }>,
            required: true,
        },
        currentStep: { type: Number as () => 1 | 2 | 3, required: true },
    },
    setup(props) {
        const statusOf = (key: number) =>
            key < props.currentStep
                ? "done"
                : key === props.currentStep
                  ? "active"
                  : "todo";

        const circleClass = (key: number) => {
            const s = statusOf(key);
            if (s === "done") return "bg-[#04C951] text-white";
            if (s === "active") return "bg-[#088BEA] text-white";
            return "bg-gray-200 text-gray-500";
        };

        const titleClass = (key: number) => {
            const s = statusOf(key);
            if (s === "todo") return "text-gray-400";
            return "text-gray-800";
        };

        const lineClass = (key: number) =>
            key < props.currentStep ? "bg-gray-300" : "bg-gray-200";

        const isDone = (key: number) => statusOf(key) === "done";

        return { circleClass, titleClass, lineClass, isDone };
    },
    template: `
        <div class="flex items-center gap-4">
            <div v-for="(s, idx) in steps" :key="s.key" class="flex items-center">
                <div class="flex items-center gap-3">
                    <div class="flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold"
                         :class="circleClass(s.key)">
                        <svg
                            v-if="isDone(s.key)"
                            class="h-[10px] w-[14px]"
                            viewBox="0 0 14 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <path
                                d="M0 5.25354L1.78843 3.4565L5.25353 6.53468L12.0462 0L13.7142 1.78844L4.99558 9.99978L0 5.25354Z"
                                fill="white"
                            />
                        </svg>
                        <span v-else>{{ s.key }}</span>
                    </div>
                    <div class="text-sm font-medium whitespace-nowrap" :class="titleClass(s.key)">
                        {{ s.title }}
                    </div>
                </div>
                <div v-if="idx !== steps.length - 1" class="mx-4 h-px w-8" :class="lineClass(s.key)"></div>
            </div>
        </div>
    `,
});

const steps: Step[] = [
    { key: 1, title: t("vue.add_your_domain") },
    { key: 2, title: t("vue.configure_dns_record") },
    { key: 3, title: t("vue.review_and_confirm") },
];

const canGoBack = computed(
    () => currentStep.value === 3 && !dnsValidated.value,
);

const showExitSetup = computed(() => {
    const count = props.domainsCount ?? 0;
    return currentStep.value === 1 && count > 0;
});

const showFinishLater = computed(() => {
    if (currentStep.value === 1) return false;
    if (currentStep.value === 3) return false;
    return true;
});

const showVerifyDnsMenu = computed(
    () => currentStep.value === 3 && !dnsValidated.value,
);

const hasGodMode = computed(() =>
    Boolean((page.props as { hasGodMode?: boolean }).hasGodMode),
);
const subscriptionLevel = computed(() =>
    String(props.subscriptionLevel ?? "trial").toLowerCase(),
);
const isTrialLevel = computed(() =>
    ["trial", "trial-extended", "trial-expired"].includes(
        subscriptionLevel.value,
    ),
);
const isCancelledLevel = computed(
    () => subscriptionLevel.value === "subscription-cancelled",
);
const isEnterpriseLevel = computed(
    () => subscriptionLevel.value === "enterprise",
);
const isFreePlanLevel = computed(() => subscriptionLevel.value === "free-plan");

const domainCount = computed(() => props.domainsCount ?? 0);
const unparkedDomainCount = computed(() => props.unparkedDomainCount ?? 0);

const canAddDomain = computed(() => {
    if (hasGodMode.value) {
        return true;
    }

    if (props.emptySlots) {
        return true;
    }

    if (isFreePlanLevel.value) {
        return unparkedDomainCount.value < 1;
    }

    if (props.isScalePlan) {
        return domainCount.value < 50;
    }

    return Boolean(props.isTrialing || props.isTrialExtended);
});

const showBillingUpdate = computed(() => {
    if (props.emptySlots > 0) return false;
    if (!props.isScalePlan) return false;
    if (props.isTrialing || props.isTrialExtended) return false;
    if (isTrialLevel.value || isCancelledLevel.value || isEnterpriseLevel.value)
        return false;
    if (hasGodMode.value) return false;
    return true;
});

const billingLimitReached = computed(
    () => !canAddDomain.value && Boolean(props.isScalePlan), // TODO: check for enterprise
);

const billingUpdateTitle = computed(() =>
    billingLimitReached.value
        ? t("vue.youve_reached_your_limit")
        : t("vue.billing_update"),
);

const billingUpdateCopy = computed(() => {
    if (!billingLimitReached.value) {
        return billingUpdateMessage.value;
    }

    if (props.isScalePlan) {
        return t("vue.cannot_add_more_domains_scale_plan");
    }

    return t("vue.reached_your_limit_body");
});

const createdAt = ref<string>(new Date().toLocaleDateString());

const dnsRefreshBusy = ref(false);

const sidebarItems = computed<string[]>(() => {
    if (currentStep.value === 1) {
        return [
            t("vue.domain_wizard_sidebar_step1_item1"),
            t("vue.domain_wizard_sidebar_step1_item2"),
            t("vue.domain_wizard_sidebar_step1_item3"),
        ];
    }

    if (currentStep.value === 2) {
        return [
            t("vue.domain_wizard_sidebar_step2_item1"),
            t("vue.domain_wizard_sidebar_step2_item2"),
            t("vue.domain_wizard_sidebar_step2_item3"),
            t("vue.domain_wizard_sidebar_step2_item4"),
        ];
    }

    if (dnsValidated.value) {
        return [
            t("vue.domain_wizard_sidebar_step3_item1"),
            t("vue.domain_wizard_sidebar_step3_item2"),
            t("vue.domain_wizard_sidebar_step3_item3"),
        ];
    }

    return [
        t("vue.domain_wizard_sidebar_step3_inactive_item1"),
        t("vue.domain_wizard_sidebar_step3_inactive_item2"),
        t("vue.domain_wizard_sidebar_step3_inactive_item3"),
    ];
});

const showCompletionNotice = computed(
    () => currentStep.value === 3 && dnsValidated.value,
);

const billingUpdateMessage = computed(() => {
    const period =
        subscriptionData.value?.period || props.subscriptionPeriod || "month";

    if (period === "year" || period === "yearly") {
        return t("vue.billing_update_yearly_message");
    }

    return t("vue.billing_update_monthly_message");
});

const billingPlanLabel = computed(() => {
    const name = subscriptionData.value?.items?.[0]?.productName;
    if (name) return name;

    const fallback = props.subscriptionLevel ?? "";
    if (!fallback) return "-";
    return fallback
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
});

const billingPeriodLabel = computed(() => {
    const period =
        subscriptionData.value?.period || props.subscriptionPeriod || "";
    if (period === "year" || period === "yearly") {
        return t("vue.billing_period_yearly");
    }
    if (period === "month" || period === "monthly") {
        return t("vue.billing_period_monthly");
    }
    return "-";
});

const billingPeriodEnd = computed(() => {
    const date = subscriptionData.value?.currentBillingPeriod?.end?.date;
    if (!date) return "-";
    return new Date(date).toLocaleDateString();
});

const billingDomainPrice = computed(() => {
    const price = subscriptionData.value?.items?.[0]?.unitPrice;
    return formatCurrency(price);
});

const billingProratedCostOld = computed(() => {
    const total = subscriptionData.value?.totals?.total;
    return formatCurrency(total);
});

const billingProratedCostNew = computed(() => {
    const total =
        subscriptionData.value?.totals?.total +
        subscriptionData.value?.items?.[0]?.unitPrice;
    return formatCurrency(total);
});

const billingUpdateItems = computed(() => [
    {
        label: t("vue.billing_update_current_plan"),
        value: billingPlanLabel.value,
    },
    {
        label: t("vue.billing_update_billing_period"),
        value: billingPeriodLabel.value,
    },
    {
        label: t("vue.billing_update_period_end"),
        value: billingPeriodEnd.value,
    },
    {
        label: t("vue.billing_update_domain_month"),
        value: billingDomainPrice.value,
    },
    {
        label: t("vue.billing_update_prorated_cost_old"),
        value: billingProratedCostOld.value,
    },
    {
        label: t("vue.billing_update_prorated_cost"),
        value: billingProratedCostNew.value,
    },
]);

const canSubmitStep1 = computed(() => {
    if (!isValidDomain(domain.value)) return false;
    if (loading.value) return false;
    if (!domainApproved.value) return false;
    if (!canAddDomain.value) return false;
    if (showBillingUpdate.value && !billingAgreement.value) return false;
    return true;
});

const domainManipulationRestricted = computed(
    () => currentStep.value === 1 && !canManipulateDomains.value,
);

const primaryDisabled = computed(() => {
    if (currentStep.value === 1) {
        if (domainExists.value) return false;
        return !canSubmitStep1.value && !domainManipulationRestricted.value;
    }

    if (currentStep.value === 2) return !domain.value;
    if (!dnsValidated.value) return dnsRefreshBusy.value || !domain.value;
    return dnsRefreshBusy.value || loading.value;
});

const primaryDisabledMessage = computed(() =>
    domainManipulationRestricted.value
        ? domainManipulationDisabledMessage.value
        : "",
);

const primaryBtnClass = computed(() => {
    if (primaryDisabled.value || domainManipulationRestricted.value) {
        return "bg-gray-300 border-[0.365px] border-gray-300";
    }
    return "bg-[#088BEA] border-[0.365px] border-[#088BEA]";
});

function formatCurrency(value?: number | null): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return "-";
    }

    return `$${Number(value).toFixed(2)}`;
}

function goBack() {
    if (currentStep.value === 3) currentStep.value = 2;
}

function exitSetup() {
    router.visit(route("domains.index"));
}

function goToExistingDomain() {
    if (!domain.value) return;
    router.visit(route("domainGuide.index", { domain: domain.value }));
}

function goToTeamMembers() {
    router.visit(route("members.index"));
}

function goNextOrFinish() {
    if (currentStep.value === 1) {
        if (domainExists.value) {
            goToExistingDomain();
            return;
        }
        addDomain();
        return;
    }

    if (currentStep.value === 2) {
        currentStep.value = 3;
        return;
    }

    if (!dnsValidated.value) {
        refreshDns();
        return;
    }

    router.visit(route("domains.index"));
}

type DnsCheckResponse = {
    success: boolean;
    result?: {
        active: boolean;
        dmarc_record?: string | null;
        dmarc_policy?: "none" | "quarantine" | "reject" | string;
    };
    checked_at?: string;
};

async function refreshDns() {
    if (!domain.value || dnsRefreshBusy.value) return;

    dnsRefreshBusy.value = true;

    try {
        const response = await axios.get<DnsCheckResponse>(
            route("domains.check-dns", { domain: domain.value }),
            { headers: { Accept: "application/json" } },
        );

        const data = response.data;
        if (!data?.success || !data.result) return;

        dnsValidated.value = Boolean(data.result.active);
        lastLookedUpDomain.value = domain.value;

        txtRecordFromServer.value = data.result.dmarc_record ?? null;

        existingDomain.value = {
            ...(existingDomain.value ?? { domain: domain.value }),
            domain: domain.value,
            active: data.result.active,
            dmarcRecord: data.result.dmarc_record ?? null,
        };

        if (dnsValidated.value) currentStep.value = 3;
    } catch (error) {
        console.error("Failed to check DNS records:", error);
    } finally {
        dnsRefreshBusy.value = false;
    }
}

function isValidDomain(input: string): boolean {
    const v = input.trim().toLowerCase();
    return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/.test(
        v,
    );
}

function normalizeDomainInput(input: string): string {
    let value = input.trim().toLowerCase();

    if (value.includes("://")) {
        try {
            const parsed = new URL(value);
            value = parsed.hostname;
        } catch (error) {
            value = value.split("://").pop() ?? value;
        }
    }

    if (value.includes("/")) {
        value = value.split("/")[0] ?? value;
    }

    return value.replace(/\.+$/, "").trim();
}

function mapDomainError(err: unknown): string {
    const e = err as Record<string, any> | undefined;
    const msgRaw = e?.domain ?? e?.errors?.domain;

    const msg = Array.isArray(msgRaw)
        ? String(msgRaw[0] ?? "")
        : String(msgRaw ?? "");
    const lower = msg.toLowerCase();

    if (
        lower.includes("taken") ||
        lower.includes("exists") ||
        lower.includes("already")
    ) {
        return t("vue.domain_wizard_domain_exists");
    }

    return t("vue.domain_wizard_domain_invalid");
}

function applyExistingDomainState(match: DomainDto) {
    existingDomain.value = match;

    domain.value = match.domain ?? "";
    createdAt.value = match.created_at
        ? new Date(match.created_at).toLocaleDateString()
        : createdAt.value;

    dnsValidated.value = Boolean(match.active);
    lastLookedUpDomain.value = domain.value;

    txtRecordFromServer.value = match.dmarcRecord ?? null;

    currentStep.value = dnsValidated.value ? 3 : 2;
}

async function findDomain() {
    if (domainCheckBusy.value) {
        return;
    }

    domainTouched.value = true;
    step1Error.value = "";

    domain.value = normalizeDomainInput(domain.value);

    if (!isValidDomain(domain.value)) {
        step1Error.value = t("vue.domain_wizard_domain_invalid");
        domainCheckStatus.value = "error";
        return;
    }

    domainCheckStatus.value = "checking";

    try {
        const response = await axios.get(
            route("domainGuide.checkDomain", { domain: domain.value }),
            {
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            },
        );

        const data = response.data ?? {};

        if (data.success === false) {
            domainCheckStatus.value = "error";
            step1Error.value =
                data.message || t("vue.domain_wizard_domain_invalid");
            return;
        }
        if (data.status === "exists") {
            if (typeof data.domain === "string") {
                domain.value = data.domain;
            }
            domainCheckStatus.value = "exists";
            return;
        }

        if (data.status === "available") {
            if (typeof data.domain === "string") {
                domain.value = data.domain;
            }
            domainCheckStatus.value = "available";
            return;
        }

        domainCheckStatus.value = "error";
        step1Error.value = t("vue.domain_wizard_domain_invalid");
    } catch (err: any) {
        const msg = err?.response?.data?.message;
        domainCheckStatus.value = "error";
        step1Error.value = msg || t("vue.domain_wizard_domain_invalid");
    }
}

function addDomain() {
    if (!ensureCanManipulateDomains()) {
        return;
    }

    domainTouched.value = true;
    step1Error.value = "";

    if (!domainApproved.value) {
        return;
    }

    if (showBillingUpdate.value && !billingAgreement.value) {
        return;
    }

    if (!isValidDomain(domain.value)) {
        step1Error.value = t("vue.domain_wizard_domain_invalid");
        return;
    }

    router.post(
        route("domains.store"),
        { domain: domain.value, redirect_to_domain_guide: true },
        {
            onBefore: () => {
                loading.value = true;
            },
            // router.post preserves state, so the page component is reused and
            // onMounted never runs again. The redirect brings the created domain
            // back in selectedDomain, and advancing the wizard is this handler's
            // job; without it the user stays on step one.
            onSuccess: (page) => {
                const created = (
                    page.props as { selectedDomain?: DomainDto | null }
                ).selectedDomain;

                if (created !== null && created !== undefined) {
                    applyExistingDomainState(created);

                    return;
                }

                createdAt.value = new Date().toLocaleDateString();
                currentStep.value = 2;
            },
            onError: (err) => {
                step1Error.value = mapDomainError(err);
            },
            onFinish: () => {
                loading.value = false;
            },
            preserveScroll: true,
        },
    );
}

onMounted(() => {
    if (props.selectedDomain) {
        applyExistingDomainState(props.selectedDomain);
    } else if (props.requestedDomain) {
        domain.value = props.requestedDomain;
    }

    if (showBillingUpdate.value) {
        loadSubscriptionData();
    }
});

watch(domain, (value, oldValue) => {
    if (value === oldValue) return;
    domainCheckStatus.value = "idle";
    step1Error.value = "";
    billingAgreement.value = false;
});

const loadSubscriptionData = async () => {
    try {
        const response = await axios.get(
            route("billing.subscriptions.dataInfo"),
        );
        subscriptionData.value = response.data.paddleSubscription;
    } catch (error) {
        console.error("Failed to load subscription data:", error);
    } finally {
        subscriptionLoading.value = false;
    }
};

const { preparedRecord } = useDmarcRecordPreparer(txtRecordFromServer);

// Seeded synchronously, not left to the watcher below: the configurator is fed
// this value as its v-model and rebuilds the record from whatever it is handed,
// so an empty string here would be echoed back as a record with no rua= tag.
txtRecordPreview.value = preparedRecord.value;
lastPreparedRecord.value = preparedRecord.value;

watch(
    preparedRecord,
    async (value) => {
        if (txtRecordPreview.value === lastPreparedRecord.value) {
            txtRecordPreview.value = value;
            await nextTick();
            lastPreparedRecord.value = txtRecordPreview.value;
        } else {
            lastPreparedRecord.value = value;
        }
    },
    { immediate: true },
);

watch(currentStep, async (newStep) => {
    if (newStep === 2) {
        await nextTick();
        lastPreparedRecord.value = txtRecordPreview.value;
    }
});

function parseDmarcPolicyFromRecord(
    record: string | null | undefined,
): "none" | "quarantine" | "reject" {
    if (!record) return "none";
    const m = record.match(/(?:^|;\s*)p\s*=\s*(none|quarantine|reject)\b/i);
    const p = (m?.[1] ?? "none").toLowerCase();
    if (p === "reject" || p === "quarantine") return p;
    return "none";
}

const dmarcPolicyFromDb = computed<"none" | "quarantine" | "reject">(() => {
    const pDb = parseDmarcPolicyFromRecord(existingDomain.value?.dmarcRecord);
    return pDb ?? "none";
});

const mailDirty = ref(false);
const developerMailBodyDraft = ref("");

const buildDeveloperMailBody = () => {
    const d = domain.value || t("vue.domain_wizard_example_domain");
    return t("vue.domain_wizard_developer_email_body", {
        domain: d,
        txtRecord: txtRecordPreview.value,
    });
};

watch(
    [domain, txtRecordPreview],
    () => {
        if (!mailDirty.value)
            developerMailBodyDraft.value = buildDeveloperMailBody();
    },
    { immediate: true },
);

watch(developerMailBodyDraft, () => {
    mailDirty.value = true;
});
</script>

<style scoped>
.btn-hover-dim {
    transition:
        filter 150ms ease,
        transform 150ms ease;
}

.btn-hover-dim:hover:not(:disabled) {
    filter: brightness(0.92);
}

.btn-hover-dim:active:not(:disabled) {
    filter: brightness(0.88);
}

.wizard-action-label-compact {
    display: none;
}

@media (min-width: 1550px) and (max-width: 1640px) {
    .wizard-sidebar {
        width: 300px;
    }

    .wizard-action-label-full {
        display: none;
    }

    .wizard-action-label-compact {
        display: inline;
    }
}

@media (max-width: 1000px) {
    .wizard-topbar {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }

    .wizard-actions {
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 10px;
    }
}

@media (max-width: 1550px) {
    .wizard-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .wizard-actions > button {
        width: 100%;
        justify-content: center;
    }

    .wizard-stepper {
        margin: auto;
    }

    :deep(.wizard-stepper .text-sm.font-medium.whitespace-nowrap) {
        display: none;
    }

    :deep(.wizard-stepper .w-8) {
        width: 20px;
    }
}

@media (max-width: 850px) {
    .wizard-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .wizard-actions > button {
        width: 100%;
        justify-content: center;
    }

    .wizard-stepper {
        margin: auto;
    }

    :deep(.wizard-stepper .text-sm.font-medium.whitespace-nowrap) {
        display: none;
    }

    :deep(.wizard-stepper .w-8) {
        width: 20px;
    }
}
</style>
